/**
 * Sprint 23.7 — `<HireSuggestionCard>`: renders the AI-suggested first hire on
 * `/welcome/hire` (step 3). A ramp-coloured monogram + name + role, a short bio,
 * and a compact trait grid (Style / Specialties / Equipped). The shape comes
 * straight from the `suggest_first_hire` MCP tool.
 */

import { type CSSProperties } from "react";
import { agentPersonaFromId, personaFill } from "../../lib/agent-persona";

export interface SuggestedAgent {
  name: string;
  role: string;
  bio: string;
  style: string;
  motivations: string;
  specialties: string[];
  equipped_with: string[];
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0) return "";
  if (parts.length === 1) return (parts[0] ?? "").slice(0, 2).toUpperCase();
  return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase();
}

function titleCase(s: string): string {
  return s.length ? s[0]!.toUpperCase() + s.slice(1) : s;
}

export function HireSuggestionCard({ suggestedAgent }: { suggestedAgent: SuggestedAgent }) {
  // RC1-1 — a pre-hire suggestion has no node id yet (the one identity surface
  // without a stable id), so it hashes the suggested name — but through the ONE
  // shared derivation, so there is no second palette to drift.
  const suggestionFill = personaFill(agentPersonaFromId(suggestedAgent.name));
  const traits: Array<{ label: string; value: string }> = [];
  if (suggestedAgent.style) traits.push({ label: "Style", value: titleCase(suggestedAgent.style) });
  if (suggestedAgent.specialties.length > 0) {
    traits.push({ label: "Specialties", value: suggestedAgent.specialties.join(", ") });
  }
  traits.push({
    label: "Equipped",
    value: suggestedAgent.equipped_with.length > 0 ? `${suggestedAgent.equipped_with.length} connection(s)` : "Nothing yet",
  });

  return (
    <div style={cardStyle}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <span
          style={{
            width: 36,
            height: 36,
            borderRadius: "50%",
            background: suggestionFill,
            color: "#fff",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 13,
            fontWeight: 500,
            flexShrink: 0,
          }}
          aria-hidden="true"
        >
          {initials(suggestedAgent.name)}
        </span>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 500, color: "var(--color-text-primary)" }}>{suggestedAgent.name}</div>
          <div style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>{suggestedAgent.role}</div>
        </div>
      </div>

      {suggestedAgent.bio && (
        <p style={{ margin: 0, fontSize: 12, color: "var(--color-text-secondary)", lineHeight: 1.5 }}>
          {suggestedAgent.bio}
        </p>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 11 }}>
        {traits.map((t) => (
          <div key={t.label} style={{ display: "flex", gap: 8 }}>
            <span style={{ minWidth: 60, color: "var(--color-text-tertiary)", flexShrink: 0 }}>{t.label}</span>
            <span style={{ color: "var(--color-text-primary)" }}>{t.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

const cardStyle: CSSProperties = {
  background: "var(--color-background-secondary)",
  borderRadius: "var(--border-radius-md)",
  padding: 14,
  display: "flex",
  flexDirection: "column",
  gap: 10,
  fontFamily: "var(--font-sans)",
};
